import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { newsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { getStorageUrl } from '../../utils/imageHelpers';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import HelpTooltip from '../../components/UI/HelpTooltip';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar, 
  User, 
  Tag,
  Image,
  FileText,
  Upload,
  X
} from 'lucide-react';

const NewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NewsCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  transition: transform 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const NewsImage = styled.div`
  width: 100%;
  height: 200px;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NewsContent = styled.div`
  padding: 1.5rem;
`;

const NewsTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
`;

const NewsMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const NewsExcerpt = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const NewsActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const CategoryTag = styled.span`
  background-color: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const EditorWrapper = styled.div`
  .quill {
    background: ${props => props.theme.colors.background.primary};
    border-radius: ${props => props.theme.borderRadius.md};
  }
  
  .ql-toolbar {
    background: ${props => props.theme.colors.background.secondary};
    border-radius: ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md} 0 0;
    border: 1px solid ${props => props.theme.colors.border.primary};
    border-bottom: none;
    padding: 0.75rem;
  }
  
  .ql-container {
    border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
    border: 1px solid ${props => props.theme.colors.border.primary};
    font-family: ${props => props.theme.typography.fontFamily.primary};
    font-size: ${props => props.theme.typography.fontSize.sm};
    min-height: 300px;
  }
  
  .ql-editor {
    min-height: 300px;
    color: ${props => props.theme.colors.text.primary};
    
    &.ql-blank::before {
      color: ${props => props.theme.colors.text.secondary};
      font-style: italic;
    }
  }
  
  .ql-stroke {
    stroke: ${props => props.theme.colors.text.secondary};
  }
  
  .ql-fill {
    fill: ${props => props.theme.colors.text.secondary};
  }
  
  .ql-picker-label {
    color: ${props => props.theme.colors.text.secondary};
  }
  
  .ql-toolbar button:hover,
  .ql-toolbar button:focus,
  .ql-toolbar .ql-picker-label:hover,
  .ql-toolbar .ql-picker-label.ql-active {
    color: ${props => props.theme.colors.primary};
    
    .ql-stroke {
      stroke: ${props => props.theme.colors.primary};
    }
    
    .ql-fill {
      fill: ${props => props.theme.colors.primary};
    }
  }
  
  .ql-toolbar button.ql-active,
  .ql-toolbar .ql-picker-item.ql-selected {
    color: ${props => props.theme.colors.primary};
    
    .ql-stroke {
      stroke: ${props => props.theme.colors.primary};
    }
    
    .ql-fill {
      fill: ${props => props.theme.colors.primary};
    }
  }
`;

const FileUpload = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  margin-bottom: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const NewsManagement = () => {
  const { theme } = useTheme();
  const { user, hasPermission } = useAuth();
  const [news, setNews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewingNews, setViewingNews] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    category: '',
    status: 'published',
    published_date: new Date().toISOString().split('T')[0],
    is_featured: false,
    tags: [],
    image: null,
    imagePreview: null
  });
  
  const categories = [
    { value: 'announcement', label: 'Announcement' },
    { value: 'event', label: 'Event' },
    { value: 'success_story', label: 'Success Story' },
    { value: 'press_release', label: 'Press Release' },
    { value: 'general', label: 'General News' }
  ];

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];
  
  // Fetch news from Laravel backend
  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await newsAPI.getAll();
      console.log('News API Response:', response.data);
      
      // Handle both paginated and non-paginated responses
      const newsData = response.data.data || response.data;
      setNews(Array.isArray(newsData) ? newsData : []);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news. Using Laravel backend.');
      setNews([]);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredNews = news.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.summary && item.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (item.excerpt && item.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === '' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFormData(prev => ({
            ...prev,
            image: file,
            imagePreview: e.target.result
          }));
        };
        reader.readAsDataURL(file);
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const newsData = {
        title: formData.title,
        summary: formData.summary,
        content: formData.content,
        category: formData.category,
        status: formData.status,
        published_date: formData.published_date,
        is_featured: formData.is_featured,
        tags: formData.tags
      };

      let response;
      if (editingNews) {
        response = await newsAPI.update(editingNews.id, newsData);
        toast.success('News updated successfully');
      } else {
        response = await newsAPI.create(newsData);
        toast.success('News created successfully');
      }

      // Upload image if provided
      if (formData.image && response.data.id) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);
        await newsAPI.uploadImage(response.data.id, imageFormData);
      }

      // Refresh news list
      await fetchNews();
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        summary: '',
        category: '',
        status: 'published',
        published_date: new Date().toISOString().split('T')[0],
        is_featured: false,
        tags: [],
        image: null,
        imagePreview: null
      });
      setShowAddModal(false);
      setEditingNews(null);
    } catch (err) {
      console.error('Error saving news:', err);
      const message = err.response?.data?.message || 'Failed to save news';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      summary: newsItem.summary || newsItem.excerpt || '',
      category: newsItem.category,
      status: newsItem.status || 'published',
      published_date: newsItem.published_date || new Date().toISOString().split('T')[0],
      is_featured: newsItem.is_featured || false,
      tags: newsItem.tags || [],
      image: null,
      imagePreview: newsItem.featured_image_url ? getStorageUrl(newsItem.featured_image_url) : null
    });
    setShowAddModal(true);
  };

  const handleViewNews = (newsItem) => {
    setViewingNews(newsItem);
    setShowViewModal(true);
  };
  
  const openDeleteDialog = (newsItem) => {
    setDeleteTarget(newsItem);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await newsAPI.delete(deleteTarget.id);
      toast.success('News deleted successfully');
      await fetchNews();
    } catch (err) {
      console.error('Error deleting news:', err);
      toast.error(err.response?.data?.message || 'Failed to delete news');
    } finally {
      setDeleteLoading(false);
      closeDeleteDialog();
    }
  };
  
  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      imagePreview: null
    }));
  };
  
  return (
    <NewsContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>
          News Management
          <HelpTooltip
            steps={[
              {
                title: 'Step 1 – Understand this page',
                content: (
                  <p>
                    Use this page to create, edit, and remove news articles that appear on the public website.
                    You can also search and filter articles by category.
                  </p>
                ),
              },
              {
                title: 'Step 2 – Create or edit news',
                content: (
                  <ul>
                    <li><strong>Add News</strong> opens a form where you can enter the title, summary, full content and category.</li>
                    <li>You can upload an image and mark an article as featured so it is highlighted on the website.</li>
                  </ul>
                ),
              },
              {
                title: 'Step 3 – Maintain content quality',
                content: (
                  <ul>
                    <li>Use clear titles and summaries so visitors quickly understand each article.</li>
                    <li>Use categories (announcement, event, success story, etc.) so content stays organised.</li>
                  </ul>
                ),
              },
            ]}
          />
        </Title>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {error && <span style={{ color: 'red', fontSize: '0.9rem' }}>{error}</span>}
          {hasPermission(['add_news', 'create_news']) && (
            <Button 
              variant="primary" 
              onClick={() => setShowAddModal(true)}
              disabled={loading}
            >
              <Plus size={20} /> Add News
            </Button>
          )}
        </div>
      </Header>
      
      <SearchContainer theme={theme}>
        <Input
          placeholder="Search news..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search size={20} />}
          style={{ minWidth: '300px' }}
        />
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </Select>
      </SearchContainer>

      {loading && <div>Loading news...</div>}
      {!loading && filteredNews.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
          No news articles found. Create your first article!
        </div>
      )}
      
      <NewsGrid theme={theme}>
        {filteredNews.map(item => (
          <NewsCard key={item.id} theme={theme}>
            <NewsImage theme={theme}>
              {item.featured_image_url ? (
                <img src={getStorageUrl(item.featured_image_url)} alt={item.title} />
              ) : item.imagePreview ? (
                <img src={item.imagePreview} alt={item.title} />
              ) : (
                <Image size={48} style={{ opacity: 0.5 }} />
              )}
            </NewsImage>
            <NewsContent theme={theme}>
              <NewsTitle theme={theme}>{item.title}</NewsTitle>
              <NewsMeta theme={theme}>
                <span><Calendar size={16} /> {new Date(item.published_date || item.created_at).toLocaleDateString()}</span>
                <span><User size={16} /> {item.author?.full_name || item.author?.fullName || 'Admin'}</span>
                <CategoryTag theme={theme}>
                  {categories.find(c => c.value === item.category)?.label || item.category}
                </CategoryTag>
              </NewsMeta>
              <NewsExcerpt theme={theme}>{item.summary || item.excerpt || 'No summary available'}</NewsExcerpt>
              <NewsActions>
                <Button variant="outline" size="small" onClick={() => handleViewNews(item)}>
                  <Eye size={16} /> View
                </Button>
                {hasPermission('edit_news') && (
                  <Button 
                    variant="outline" 
                    size="small"
                    onClick={() => handleEdit(item)}
                  >
                    <Edit size={16} />
                  </Button>
                )}
                {hasPermission('delete_news') && (
                  <Button 
                    variant="outline" 
                    size="small"
                    onClick={() => openDeleteDialog(item)}
                    style={{ color: theme.colors.error }}
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </NewsActions>
            </NewsContent>
          </NewsCard>
        ))}
      </NewsGrid>
      
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingNews(null);
          setFormData({
            title: '',
            content: '',
            summary: '',
            category: '',
            status: 'published',
            published_date: new Date().toISOString().split('T')[0],
            is_featured: false,
            tags: [],
            image: null,
            imagePreview: null
          });
        }}
        title={editingNews ? 'Edit News' : 'Add News'}
      >
        <form onSubmit={handleSubmit}>
          <ModalContent theme={theme}>
            <FormGroup theme={theme}>
              <Label theme={theme}>Title</Label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter news title"
                required
              />
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Summary/Excerpt</Label>
              <TextArea
                name="summary"
                value={formData.summary}
                onChange={handleInputChange}
                placeholder="Enter news summary (brief overview)"
                required
              />
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Content</Label>
              <EditorWrapper theme={theme}>
                <ReactQuill 
                  value={formData.content}
                  onChange={(content) => setFormData({...formData, content})}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your news content here... You can add images, format text, create lists, and more!"
                  theme="snow"
                />
              </EditorWrapper>
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Category</Label>
              <Select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleInputChange}
                  style={{ marginRight: '0.5rem' }}
                />
                Feature this article
              </Label>
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Image</Label>
              {formData.imagePreview ? (
                <ImagePreview theme={theme}>
                  <img src={formData.imagePreview} alt="Preview" />
                  <RemoveImageButton theme={theme} onClick={removeImage}>
                    <X size={16} />
                  </RemoveImageButton>
                </ImagePreview>
              ) : (
                <FileUpload theme={theme}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleInputChange}
                    style={{ display: 'none' }}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload">
                    <Upload size={32} style={{ marginBottom: '0.5rem' }} />
                    <div>Click to upload image</div>
                    <div style={{ fontSize: theme.typography.fontSize.sm, opacity: 0.7 }}>
                      PNG, JPG up to 10MB
                    </div>
                  </label>
                </FileUpload>
              )}
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setShowAddModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving...' : (editingNews ? 'Update' : 'Publish')} News
              </Button>
            </div>
          </ModalContent>
        </form>
      </Modal>

      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewingNews(null);
        }}
        title={viewingNews?.title || 'Preview News'}
      >
        {viewingNews && (
          <ModalContent theme={theme}>
            <NewsMeta theme={theme}>
              <span><Calendar size={16} /> {new Date(viewingNews.published_date || viewingNews.created_at).toLocaleDateString()}</span>
              <span><User size={16} /> {viewingNews.author?.full_name || viewingNews.author?.fullName || 'Admin'}</span>
              {viewingNews.category && (
                <CategoryTag theme={theme}>
                  {categories.find(c => c.value === viewingNews.category)?.label || viewingNews.category}
                </CategoryTag>
              )}
            </NewsMeta>
            {(viewingNews.summary || viewingNews.excerpt) && (
              <NewsExcerpt theme={theme}>
                {viewingNews.summary || viewingNews.excerpt}
              </NewsExcerpt>
            )}
            <div
              style={{
                borderTop: `1px solid ${theme.colors.border.primary}`,
                paddingTop: '1rem',
                color: theme.colors.text.primary,
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: viewingNews.content || 'No content available.' }}
            />
          </ModalContent>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="Delete News"
        message={deleteTarget ? `Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.` : 'Are you sure you want to delete this news item?'}
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={closeDeleteDialog}
      />
    </NewsContainer>
  );
};

export default NewsManagement;



